import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./Hero.css";
import heroImage from "../../assets/heroimage.webp";
import icon1 from "../../assets/icon1.webp";
import icon2 from "../../assets/icon2.webp";
import icon3 from "../../assets/icon3.webp";
import missionIcon from "../../assets/mission.webp";
import visionIcon from "../../assets/vision.webp";
import valuesIcon from "../../assets/values.webp";
import quadrant1 from "../../assets/quadrant1.webp";
import quadrant2 from "../../assets/quadrant2.webp";
import quadrant3 from "../../assets/quadrant3.webp";
import quadrant4 from "../../assets/quadrant4.webp";
import questionMark from "../../assets/question.webp";
const Hero = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null); // State for hover

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const scaleUp = {
    hidden: { scale: 0.95, opacity: 0 },
    visible: { scale: 1, opacity: 1, transition: { duration: 0.4 } },
  };

  // Card content animation variants (used for image <-> text swap)
  // Simplified: soft fade/scale instead of large side swipe
  const swipeVariants = {
    initial: { opacity: 0, scale: 0.95 },
    animate: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.2, ease: "easeIn" },
    },
  };

  // Content for Mission, Vision, Values
  const mvvContent = [
    {
      title: "Mission",
      text: "At BinRashid Group Egypt, our mission is to empower businesses to thrive in a dynamic and competitive landscape by delivering comprehensive, innovative, and tailored business solutions. We are committed to providing exceptional services across finance, accounting, auditing, legal consultation, sales, and business development, ensuring our clients achieve sustainable growth and operational excellence.",
    },
    {
      title: "Vision",
      text: "To transform the way businesses operate by delivering integrated, innovative solutions that unlock potential and drive sustainable growth. We aspire to be the driving force behind our clients' success, setting new standards of excellence and professionalism.",
    },
    {
      title: "Values",
      text: "Thought Partnership, Leadership, Be the Solution, Deliver with Excellence, Learn and Teach, Diversity",
    },
  ];

  return (
    <>
      <motion.section
        className="hero-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="hero-image-container">
          <img src={heroImage} alt="Hero" className="hero-image" />
        </div>
      </motion.section>

      <motion.section
        className="growth-section"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
      >
        <div className="growth-header">
          <h2>Powering Growth Across MENA</h2>

          <motion.a
            href="/free-consultation"
            className="consult-btn"
            whileHover={{
              scale: 1.05,
              boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)",
            }}
            whileTap={{ scale: 0.98 }}
          >
            FREE CONSULTATION
          </motion.a>
        </div>
        <p className="growth-description">
          We own and operate across Egypt, Saudi Arabia, and the UAE, with 6
          successful companies thriving in diverse sectors. This wide presence
          and hands-on experience across industries grant us unmatched market
          intelligence, operational agility, and a deep understanding of each
          sector's challenges and opportunities. That means we don't just offer
          services — we deliver proven, localized, and strategic solutions that
          give your business a real head start.
        </p>

        <motion.div
          className="growth-icons"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
        >
          <motion.div className="growth-icon-block" variants={fadeIn}>
            <motion.img
              loading="lazy"
              src={icon1}
              alt="Icon 1"
              whileHover={{
                rotate: 360,
                filter: "brightness(0.8)",
                transition: {
                  rotate: { duration: 0.8, ease: "linear", repeat: Infinity },
                  filter: { duration: 0.3 },
                },
              }}
            />
            <p>
              <strong>Looking To Grow Your Business Into New Regions?</strong>
            </p>
          </motion.div>
          <motion.div className="growth-icon-block" variants={fadeIn}>
            <motion.img
              loading="lazy"
              src={icon2}
              alt="Icon 2"
              whileHover={{
                rotate: 360,
                filter: "brightness(0.8)",
                transition: {
                  rotate: { duration: 0.8, ease: "linear", repeat: Infinity },
                  filter: { duration: 0.3 },
                },
              }}
            />
            <p>
              <strong>Ready To Launch A Startup In The Middle East?</strong>
            </p>
          </motion.div>
          <motion.div className="growth-icon-block" variants={fadeIn}>
            <motion.img
              loading="lazy"
              src={icon3}
              alt="Icon 3"
              whileHover={{
                rotate: 360,
                filter: "brightness(0.8)",
                transition: {
                  rotate: { duration: 0.8, ease: "linear", repeat: Infinity },
                  filter: { duration: 0.3 },
                },
              }}
            />
            <p>
              <strong>
                Struggling With The Practicalities Of Market Entry?
              </strong>
            </p>
          </motion.div>
        </motion.div>
      </motion.section>

      <motion.section
        className="services-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
      >
        <h3 className="services-title">Services We Help You Through</h3>
        <motion.div
          className="services-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          {[
            "Legal Registration & Licensing",
            "Office Setup & Infrastructure",
            "Support",
            "Financial Structuring & Banking",
            "Compliance & Regulatory",
            "Market Entry",
          ].map((service, index) => (
            <motion.div
              key={index}
              className="service-box"
              variants={scaleUp}
              whileHover={{ y: -5, boxShadow: "0 8px 20px rgba(0, 0, 0, 0.2)" }}
            >
              {service}
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      <div className="scrollable-container">
        <div className="scroll-arrow">➔</div>
        <motion.section
          className="scrollable-info"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="info-block">
            <h4>Looking To Grow Your Business Into New Regions?</h4>
            <p>
              Taking your company into Egypt, the UAE, or Saudi Arabia can be
              complex, but we simplify the process. Our team handles legal
              approvals, financial setup, licensing, and market entry strategies
              so you can expand smoothly and efficiently. We also assist with
              UAE or Saudi residency visas, helping you establish a long-term
              presence for sustained growth.
            </p>
          </div>
          <div className="info-block">
            <h4>Ready To Launch A Startup In The Middle East?</h4>
            <p>
              We provide end-to-end support for entrepreneurs and startups, from
              choosing the right jurisdiction to business structuring, branding,
              and go-to-market strategies. With extensive regional insights, we
              help ensure your startup gets off the ground successfully.
            </p>
          </div>
          <div className="info-block">
            <h4>Struggling With Regulatory & Market Entry Barriers?</h4>
            <p>
              Our specialists guide you through complex compliance frameworks
              and entry protocols. We assist in documentation, translation,
              legal guidance, and official interactions so your entry into MENA
              markets is smooth and secure.
            </p>
          </div>
        </motion.section>
      </div>

      <motion.section
        className="mission-vision-values"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
      >
        <h3 className="mvv-title">MISSION, VISION & VALUES</h3>
        <p className="mvv-helper-text">
          Hover or tap a card to reveal the details behind it.
        </p>
        <motion.div
          className="mvv-cards"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {mvvContent.map((item, index) => (
            <motion.div
              key={index}
              className="mvv-card"
              style={{ "--i": index }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <AnimatePresence mode="wait">
                {hoveredIndex === index ? (
                  <motion.div
                    className="mvv-text"
                    key="text"
                    variants={swipeVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                  >
                    <h4>{item.title}</h4>
                    <p>{item.text}</p>
                  </motion.div>
                ) : (
                  <motion.img
                    loading="lazy"
                    key="image"
                    src={[missionIcon, visionIcon, valuesIcon][index]}
                    alt={item.title}
                    variants={swipeVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                  />
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      <motion.section
        className="why-choose"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
      >
        <div className="why-choose-grid">
          <div className="why-choose-left">
            {[quadrant1, quadrant2, quadrant3, quadrant4].map((img, index) => (
              <motion.img
                loading="lazy"
                key={index}
                src={img}
                alt={`Quadrant ${index + 1}`}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              />
            ))}
          </div>
          <div className="why-choose-right">
            <motion.img
              loading="lazy"
              src={questionMark}
              alt="Why Choose Bin Rashid Group"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              whileHover={{ scale: 1.05 }}
            />
          </div>
        </div>
      </motion.section>
    </>
  );
};

export default Hero;
